import { RuleCheckOutcome, RuleValidationMethod } from '@atlas/types'
import type { RuleResultOutcome, RuleResultProvenance, RuleValidationResultRow } from '@atlas/ui-domain'
import type { RuleValidationRecord } from '@/services/rule-validation.service'
import { toRuleListSeverity } from './rule-list-item'

/** `RuleCheckOutcome` onto the table's own union, exhaustively — a new outcome fails here, not in the table. */
const OUTCOME: Record<RuleCheckOutcome, RuleResultOutcome> = {
	[RuleCheckOutcome.PENDING]: 'pending',
	[RuleCheckOutcome.PASS]: 'pass',
	[RuleCheckOutcome.FAIL]: 'fail',
	[RuleCheckOutcome.NEEDS_REVIEW]: 'needs_review',
}

/**
 * One audit record as the results table states it.
 *
 * Every field comes from the RECORD, never from the live rule: the statement and severity
 * are the snapshot taken when the run started, which is what keeps a dated report readable
 * after the rule is edited.
 */
export const toRuleValidationRow = (record: RuleValidationRecord): RuleValidationResultRow => ({
	id: record.resultId,
	statement: record.ruleStatement,
	severity: toRuleListSeverity(record.ruleSeverity),
	// A record with no subject was resolved against the target itself, not against a
	// piece of content inside it. Naming that is the difference between "nothing was
	// judged" and "the target as a whole was".
	subject: record.subjectLabel ?? 'Entire target',
	elementType: record.elementType,
	outcome: OUTCOME[record.outcome],
	rationale: record.rationale,
	evidence: record.evidence,
	evaluatedAt: record.evaluatedAt,
	provenance: toProvenance(record),
})

export const toRuleValidationRows = (records: RuleValidationRecord[]): RuleValidationResultRow[] =>
	records.map(toRuleValidationRow)

/**
 * How the verdict was produced — keyed off the rule's DECLARED method, never off which of
 * `model`/`promptVersion` happen to be filled in: a `multimodal-LLM` rule whose subject
 * could not be read still IS a model check, just one with no verdict yet, and stating it
 * as `deterministic` would attribute the (non-)verdict to a check that never ran.
 */
const toProvenance = (record: RuleValidationRecord): RuleResultProvenance => {
	if (record.ruleValidationMethod === RuleValidationMethod.MULTIMODAL_LLM) {
		return record.model && record.promptVersion
			? { kind: 'model', model: record.model, promptVersion: record.promptVersion }
			: { kind: 'not_evaluated', method: 'model' }
	}
	return record.ruleMetadataCheck
		? { kind: 'deterministic', field: record.ruleMetadataCheck.field, operator: record.ruleMetadataCheck.operator }
		: { kind: 'not_evaluated', method: 'deterministic' }
}
